/**
 * @fileoverview Work Order frontend service
 *
 * API client for all work order CRUD, lifecycle, cost, photo,
 * comment, stats, vendor, and auto-rule operations.
 *
 * @author FacilityAI
 * @version 1.0.0
 */

import { CORE_URL } from "@/constants/api";
import { VITE_CORE_API_URL } from "@/constants/env";
import { auth } from "@/services/auth-service";

const BASE = `${VITE_CORE_API_URL}${CORE_URL}/organization`;

// ─── helpers ────────────────────────────────────────────────────────────────

async function getToken(): Promise<string> {
	const token = await auth?.currentUser?.getIdToken();
	if (!token) throw new Error("Authentication required");
	return token;
}

async function apiGet<T>(path: string): Promise<T> {
	const token = await getToken();
	const res = await fetch(`${BASE}${path}`, {
		method: "GET",
		headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
	});
	const json = await res.json();
	if (!json.success) throw new Error(json.message ?? "Request failed");
	return json.data;
}

async function apiPost<T>(path: string, body: unknown): Promise<T> {
	const token = await getToken();
	const res = await fetch(`${BASE}${path}`, {
		method: "POST",
		headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	const json = await res.json();
	if (!json.success) throw new Error(json.message ?? "Request failed");
	return json.data;
}

async function apiPut<T>(path: string, body: unknown): Promise<T> {
	const token = await getToken();
	const res = await fetch(`${BASE}${path}`, {
		method: "PUT",
		headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	const json = await res.json();
	if (!json.success) throw new Error(json.message ?? "Request failed");
	return json.data;
}

async function apiDelete<T>(path: string): Promise<T> {
	const token = await getToken();
	const res = await fetch(`${BASE}${path}`, {
		method: "DELETE",
		headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
	});
	const json = await res.json();
	if (!json.success) throw new Error(json.message ?? "Request failed");
	return json.data;
}

// ─── Work Order types (mirror backend models for frontend use) ──────────────

export type WorkOrderType = "EMERGENCY" | "CORRECTIVE" | "PREVENTIVE" | "INSPECTION";
export type WorkOrderPriority = 1 | 2 | 3 | 4 | 5;
export type WorkOrderStatus = "DRAFT" | "OPEN" | "ASSIGNED" | "IN_PROGRESS" | "ON_HOLD" | "COMPLETED" | "CLOSED" | "CANCELLED";
export type WorkOrderSource = "MANUAL" | "AI_SUGGESTED" | "AUTO_RULE" | "INTEGRATION";
export type CostItemType = "LABOR" | "PARTS" | "VENDOR_INVOICE" | "OTHER";
export type PhotoType = "BEFORE" | "DURING" | "AFTER" | "EVIDENCE" | "OTHER";

export interface WorkOrder {
	id: number;
	organizationId: number;
	locationId: number;
	assetId: number | null;
	workOrderNumber: string;
	title: string;
	description: string | null;
	workType: WorkOrderType;
	priority: WorkOrderPriority;
	status: WorkOrderStatus;
	source: WorkOrderSource;
	assignedType: string;
	assignedToUserId: number | null;
	assignedToVendorId: number | null;
	createdByUserId: number;
	dueDate: string | null;
	slaDeadline: string | null;
	startedAt: string | null;
	completedAt: string | null;
	closedAt: string | null;
	estimatedCost: number | null;
	actualCost: number;
	estimatedLaborHours: number | null;
	actualLaborHours: number;
	externalWorkOrderId: string | null;
	rootCause: string | null;
	resolutionNotes: string | null;
	createdAt: string;
	updatedAt: string;
	// Aggregate fields
	locationName?: string;
	assetTag?: string;
	assetTypeName?: string;
	assignedUserName?: string;
	assignedVendorName?: string;
	createdByUserName?: string;
	isSlaBreached?: boolean;
	costItems?: CostItem[];
	photos?: WOPhoto[];
	activityLog?: ActivityLogEntry[];
}

export interface CostItem {
	id: number;
	workOrderId: number;
	costType: CostItemType;
	description: string | null;
	quantity: number;
	unitCost: number;
	totalCost: number;
	hours: number | null;
	rate: number | null;
	createdAt: string;
}

export interface WOPhoto {
	id: number;
	workOrderId: number;
	photoUrl: string;
	photoType: PhotoType;
	caption: string | null;
	createdAt: string;
}

export interface ActivityLogEntry {
	id: number;
	workOrderId: number;
	userId: number | null;
	action: string;
	details: Record<string, unknown>;
	comment: string | null;
	isSystemEvent: boolean;
	createdAt: string;
	userName?: string;
}

export interface WorkOrderStats {
	total: number;
	open: number;
	assigned: number;
	inProgress: number;
	onHold: number;
	completed: number;
	closed: number;
	cancelled: number;
	overdue: number;
	avgCompletionHours: number | null;
	totalCost: number;
}

export interface Vendor {
	id: number;
	organizationId: number;
	name: string;
	contactName: string | null;
	contactEmail: string | null;
	contactPhone: string | null;
	specialty: string | null;
	hourlyRate: number | null;
	isActive: boolean;
	createdAt: string;
}

export interface AutoRule {
	id: number;
	organizationId: number;
	name: string;
	description: string | null;
	ruleType: string;
	targetType: string | null;
	targetId: number | null;
	triggerConfig: Record<string, unknown>;
	woTemplate: Record<string, unknown>;
	isActive: boolean;
	lastTriggeredAt: string | null;
	nextTriggerAt: string | null;
	createdAt: string;
}

// ─── Work Order CRUD ────────────────────────────────────────────────────────

export interface WorkOrderListResponse {
	workOrders: WorkOrder[];
	total: number;
	page: number;
	limit: number;
}

export async function listWorkOrders(params: Record<string, string | number | boolean> = {}): Promise<WorkOrderListResponse> {
	const qs = new URLSearchParams();
	for (const [k, v] of Object.entries(params)) {
		if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
	}
	const path = `/work-orders${qs.toString() ? `?${qs}` : ""}`;
	return apiGet<WorkOrderListResponse>(path);
}

export async function getWorkOrder(id: number): Promise<WorkOrder> {
	return apiGet<WorkOrder>(`/work-orders/${id}`);
}

export async function getWorkOrdersByLocation(locationId: number): Promise<{ workOrders: WorkOrder[] }> {
	return apiGet<{ workOrders: WorkOrder[] }>(`/work-orders/location/${locationId}`);
}

export async function getWorkOrdersByAsset(assetId: number): Promise<{ workOrders: WorkOrder[] }> {
	return apiGet<{ workOrders: WorkOrder[] }>(`/work-orders/asset/${assetId}`);
}

export async function getWorkOrderStats(locationId?: number): Promise<WorkOrderStats> {
	const path = locationId ? `/work-orders/stats?locationId=${locationId}` : "/work-orders/stats";
	return apiGet<WorkOrderStats>(path);
}

export async function getOverdueWorkOrders(): Promise<{ workOrders: WorkOrder[]; total: number }> {
	return apiGet<{ workOrders: WorkOrder[]; total: number }>("/work-orders/overdue");
}

export async function getAssetsByLocation(locationId: number): Promise<{ assets: { id: number; assetTag: string; typeName: string | null }[] }> {
	return apiGet<{ assets: { id: number; assetTag: string; typeName: string | null }[] }>(`/work-orders/assets-by-location/${locationId}`);
}

export async function createWorkOrder(input: {
	locationId: number;
	assetId?: number | null;
	title: string;
	description?: string;
	workType: WorkOrderType;
	priority: WorkOrderPriority;
	source?: WorkOrderSource;
	assignedToUserId?: number | null;
	assignedToVendorId?: number | null;
	assignedToFlOrgVendorId?: number | null;
	dueDate?: string | null;
	estimatedCost?: number | null;
	estimatedLaborHours?: number | null;
}): Promise<WorkOrder> {
	return apiPost<WorkOrder>("/work-orders", input);
}

export async function updateWorkOrder(id: number, input: Record<string, unknown>): Promise<WorkOrder> {
	return apiPut<WorkOrder>(`/work-orders/${id}`, input);
}

export async function updateWorkOrderStatus(id: number, status: WorkOrderStatus): Promise<WorkOrder> {
	return apiPut<WorkOrder>(`/work-orders/${id}/status`, { status });
}

export async function assignWorkOrder(id: number, assignToUserId?: number | null, assignToVendorId?: number | null, assignToFlOrgVendorId?: number | null): Promise<WorkOrder> {
	return apiPut<WorkOrder>(`/work-orders/${id}/assign`, { assignToUserId, assignToVendorId, assignToFlOrgVendorId });
}

export async function deleteWorkOrder(id: number): Promise<void> {
	await apiDelete(`/work-orders/${id}`);
}

// ─── Comments ───────────────────────────────────────────────────────────────

export async function addComment(workOrderId: number, comment: string): Promise<ActivityLogEntry> {
	return apiPost<ActivityLogEntry>(`/work-orders/${workOrderId}/comments`, { comment });
}

export async function getActivityLog(workOrderId: number): Promise<{ activityLog: ActivityLogEntry[] }> {
	return apiGet<{ activityLog: ActivityLogEntry[] }>(`/work-orders/${workOrderId}/activity`);
}

// ─── Cost Items ─────────────────────────────────────────────────────────────

export async function addCostItem(workOrderId: number, input: {
	costType: CostItemType;
	description?: string;
	quantity?: number;
	unitCost?: number;
	totalCost: number;
	hours?: number;
	rate?: number;
}): Promise<CostItem> {
	return apiPost<CostItem>(`/work-orders/${workOrderId}/costs`, input);
}

export async function getCostItems(workOrderId: number): Promise<{ costItems: CostItem[] }> {
	return apiGet<{ costItems: CostItem[] }>(`/work-orders/${workOrderId}/costs`);
}

export async function deleteCostItem(workOrderId: number, costItemId: number): Promise<void> {
	await apiDelete(`/work-orders/${workOrderId}/costs/${costItemId}`);
}

// ─── Photos ─────────────────────────────────────────────────────────────────

export async function addPhoto(workOrderId: number, input: {
	photoUrl: string;
	photoType?: PhotoType;
	caption?: string;
}): Promise<WOPhoto> {
	return apiPost<WOPhoto>(`/work-orders/${workOrderId}/photos`, input);
}

export async function getPhotos(workOrderId: number): Promise<{ photos: WOPhoto[] }> {
	return apiGet<{ photos: WOPhoto[] }>(`/work-orders/${workOrderId}/photos`);
}

// ─── Vendors ────────────────────────────────────────────────────────────────

export async function listVendors(includeInactive = false): Promise<{ vendors: Vendor[] }> {
	return apiGet<{ vendors: Vendor[] }>(`/work-order-vendors${includeInactive ? "?includeInactive=true" : ""}`);
}

export async function getVendor(id: number): Promise<Vendor> {
	return apiGet<Vendor>(`/work-order-vendors/${id}`);
}

export async function createVendor(input: {
	name: string;
	contactName?: string;
	contactEmail?: string;
	contactPhone?: string;
	specialty?: string;
	hourlyRate?: number;
}): Promise<Vendor> {
	return apiPost<Vendor>("/work-order-vendors", input);
}

export async function updateVendor(id: number, input: Record<string, unknown>): Promise<Vendor> {
	return apiPut<Vendor>(`/work-order-vendors/${id}`, input);
}

export async function deleteVendor(id: number): Promise<void> {
	await apiDelete(`/work-order-vendors/${id}`);
}

// ─── Auto Rules ─────────────────────────────────────────────────────────────

export async function listAutoRules(): Promise<{ rules: AutoRule[] }> {
	return apiGet<{ rules: AutoRule[] }>("/work-order-rules");
}

export async function getAutoRule(id: number): Promise<AutoRule> {
	return apiGet<AutoRule>(`/work-order-rules/${id}`);
}

export async function createAutoRule(input: {
	name: string;
	description?: string;
	ruleType: string;
	targetType?: string;
	targetId?: number;
	triggerConfig: Record<string, unknown>;
	woTemplate: Record<string, unknown>;
}): Promise<AutoRule> {
	return apiPost<AutoRule>("/work-order-rules", input);
}

export async function updateAutoRule(id: number, input: Record<string, unknown>): Promise<AutoRule> {
	return apiPut<AutoRule>(`/work-order-rules/${id}`, input);
}

export async function toggleAutoRule(id: number): Promise<AutoRule> {
	return apiPut<AutoRule>(`/work-order-rules/${id}/toggle`, {});
}

export async function triggerAutoRule(id: number): Promise<{ created: boolean; workOrderId?: number; reason?: string }> {
	return apiPost<{ created: boolean; workOrderId?: number; reason?: string }>(`/work-order-rules/${id}/trigger`, {});
}

export async function deleteAutoRule(id: number): Promise<void> {
	await apiDelete(`/work-order-rules/${id}`);
}

// ─── Work Order Quotes / RFQ ────────────────────────────────────────────────

export type QuoteRequestStatus = "OPEN" | "CLOSED" | "AWARDED" | "CANCELLED";
export type VendorQuoteStatus = "PENDING" | "SUBMITTED" | "ACCEPTED" | "DECLINED" | "WITHDRAWN";

export interface QuoteRequest {
	id: number;
	workOrderId: number;
	organizationId: number;
	requestedByUserId: number;
	title: string;
	description: string | null;
	scopeOfWork: string | null;
	deadline: string | null;
	status: QuoteRequestStatus;
	acceptedQuoteId: number | null;
	createdAt: string;
	updatedAt: string;
}

export interface VendorQuoteWithVendor {
	id: number;
	quoteRequestId: number;
	orgVendorId: number;
	vendorId: number;
	status: VendorQuoteStatus;
	quotedAmount: number | null;
	estimatedHours: number | null;
	estimatedDays: number | null;
	proposalNotes: string | null;
	vendorRespondedAt: string | null;
	createdAt: string;
	updatedAt: string;
	companyName: string;
	contactName: string | null;
	contactEmail: string | null;
	contactPhone: string | null;
	trades: { id: number; name: string; slug: string }[];
}

export interface QuoteRequestWithQuotes extends QuoteRequest {
	quotes: VendorQuoteWithVendor[];
}

export async function createQuoteRequest(input: {
	workOrderId: number;
	title: string;
	description?: string;
	scopeOfWork?: string;
	deadline?: string;
	vendorOrgIds: number[];
}): Promise<QuoteRequestWithQuotes> {
	return apiPost<QuoteRequestWithQuotes>("/work-order-quotes", input);
}

export async function getQuoteRequestsByWorkOrder(workOrderId: number): Promise<QuoteRequestWithQuotes[]> {
	const data = await apiGet<{ quoteRequests: QuoteRequestWithQuotes[] }>(`/work-order-quotes/work-order/${workOrderId}`);
	return data.quoteRequests;
}

export async function getQuoteRequestDetail(requestId: number): Promise<QuoteRequestWithQuotes> {
	return apiGet<QuoteRequestWithQuotes>(`/work-order-quotes/${requestId}`);
}

export async function submitVendorQuote(quoteId: number, input: {
	quotedAmount: number;
	estimatedHours?: number;
	estimatedDays?: number;
	proposalNotes?: string;
}): Promise<VendorQuoteWithVendor> {
	return apiPut<VendorQuoteWithVendor>(`/work-order-quotes/quotes/${quoteId}/submit`, input);
}

export async function acceptQuote(requestId: number, quoteId: number): Promise<QuoteRequestWithQuotes> {
	return apiPut<QuoteRequestWithQuotes>(`/work-order-quotes/${requestId}/accept/${quoteId}`, {});
}

export async function cancelQuoteRequest(requestId: number): Promise<QuoteRequestWithQuotes> {
	return apiPut<QuoteRequestWithQuotes>(`/work-order-quotes/${requestId}/cancel`, {});
}
